import { Alert, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import CardContainer from '../../../CardContainer/CardContainer';
import { OrganisationCodeSelector } from '../OrganisationCodeSelector';
import WorkflowButton from './WorkflowButton';

export type TOrganisationCodeWorkflowType = 'DEFAULT' | 'INDIVIDUAL_ONLY' | 'ORGANISATION_ONLY';

const WORKFLOW_LABELS: Record<TOrganisationCodeWorkflowType, string> = {
  DEFAULT: 'DEFAULT',
  INDIVIDUAL_ONLY: 'INDIVIDUAL ONLY',
  ORGANISATION_ONLY: 'ORGANISATION ONLY',
};

export interface OrganisationCodeWorkflowSwitcherProps {
  selectedWorkflow: TOrganisationCodeWorkflowType;
  onClick: (workflow: TOrganisationCodeWorkflowType) => void | Promise<void>;
  organisationCodes: string[];
  selectedCode?: string;
  onCodeChange?: (code: string) => void;
  dataTestId?: string;
  disabledWorkflows?: Record<TOrganisationCodeWorkflowType, boolean>;
  noCodesMessage?: React.ReactNode;
}

export default function OrganisationCodeWorkflowSwitcher({
  selectedWorkflow,
  onClick,
  organisationCodes,
  selectedCode = '',
  onCodeChange,
  dataTestId,
  disabledWorkflows = {
    DEFAULT: false,
    INDIVIDUAL_ONLY: false,
    ORGANISATION_ONLY: false,
  },
  noCodesMessage = 'No organisation codes available. This may indicate a data issue. Please contact the development team for assistance.',
}: Readonly<OrganisationCodeWorkflowSwitcherProps>) {
  const [isHandling, setIsHandling] = useState(false);

  const handleClick = async (workflow: TOrganisationCodeWorkflowType) => {
    setIsHandling(true);
    try {
      await onClick(workflow);
    } finally {
      setIsHandling(false);
    }
  };

  return (
    <CardContainer
      data-testid={dataTestId}
      sx={(theme) => ({
        padding: theme.semantic.spacing.base,
        borderRadius: theme.semantic.borderRadius.lg,
      })}
      elevation={2}
    >
      <Typography variant="h6" sx={(theme) => ({ marginBottom: theme.semantic.spacing.base })}>
        Switch Workflow
      </Typography>

      {organisationCodes.length === 0 && (
        <Alert severity="error">
          {noCodesMessage}
        </Alert>
      )}

      {organisationCodes.length > 0 && (
        <>
          <OrganisationCodeSelector
            codes={organisationCodes}
            selectedCode={selectedCode}
            onCodeChange={onCodeChange}
            dataTestId={dataTestId ? `${dataTestId}-code-selector` : undefined}
          />

          <Stack
            direction="row"
            spacing={1}
            sx={(theme) => ({
              alignItems: 'stretch',
              marginTop: theme.semantic.spacing.base,
            })}
          >
            <WorkflowButton
              workflow="DEFAULT"
              isSelected={selectedWorkflow === 'DEFAULT'}
              label={WORKFLOW_LABELS.DEFAULT}
              onClick={handleClick}
              disabled={isHandling || disabledWorkflows.DEFAULT}
            />
            <WorkflowButton
              workflow="INDIVIDUAL_ONLY"
              isSelected={selectedWorkflow === 'INDIVIDUAL_ONLY'}
              label={WORKFLOW_LABELS.INDIVIDUAL_ONLY}
              onClick={handleClick}
              disabled={isHandling || disabledWorkflows.INDIVIDUAL_ONLY}
            />
            <WorkflowButton
              workflow="ORGANISATION_ONLY"
              isSelected={selectedWorkflow === 'ORGANISATION_ONLY'}
              label={WORKFLOW_LABELS.ORGANISATION_ONLY}
              onClick={handleClick}
              disabled={isHandling || disabledWorkflows.ORGANISATION_ONLY}
            />
          </Stack>
        </>
      )}
    </CardContainer>
  );
}
